import React, { useState, useMemo } from 'react';
import { X, MagnifyingGlass, UserCircle, PaperPlaneRight, Check, CheckSquare, Square } from '@phosphor-icons/react';

/**
 * MultiSelectForwardModal
 * Reusable modal to forward a message to multiple chat rooms at once.
 *
 * Props:
 *   open: boolean
 *   chats: array of { id, customer_name, chat_type }
 *   onClose: () => void
 *   onForward: (selectedIds: string[]) => Promise<void>
 *   accentColor: string (tailwind hex bg), default '#0A6F67'
 */
const MultiSelectForwardModal = ({
  open,
  chats = [],
  onClose,
  onForward,
  accentColor = '#0A6F67',
}) => {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);

  const filteredChats = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter(c =>
      (c.customer_name || '').toLowerCase().includes(q) ||
      (c.chat_type || '').toLowerCase().includes(q)
    );
  }, [chats, search]);

  const allFilteredSelected =
    filteredChats.length > 0 && filteredChats.every(c => selected.has(c.id));

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleSelectAllFiltered = () => {
    const next = new Set(selected);
    if (allFilteredSelected) {
      filteredChats.forEach(c => next.delete(c.id));
    } else {
      filteredChats.forEach(c => next.add(c.id));
    }
    setSelected(next);
  };

  const clearAll = () => setSelected(new Set());

  const handleClose = () => {
    if (sending) return;
    setSelected(new Set());
    setSearch('');
    onClose && onClose();
  };

  const handleSendToSelected = async () => {
    if (selected.size === 0) return;
    setSending(true);
    try {
      await onForward(Array.from(selected));
      setSelected(new Set());
      setSearch('');
    } finally {
      setSending(false);
    }
  };

  const handleSendToAll = async () => {
    if (chats.length === 0) return;
    setSending(true);
    try {
      await onForward(chats.map(c => c.id));
      setSelected(new Set());
      setSearch('');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      data-testid="forward-modal-overlay"
    >
      <div className="bg-wa-panel rounded-xl w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold" data-testid="forward-modal-title">
              Forward Message
            </h3>
            <p className="text-xs text-wa-text-secondary mt-0.5">
              {selected.size > 0
                ? `${selected.size} selected`
                : `Select one or more chats`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-wa-text-secondary hover:text-wa-text-primary p-1 rounded-full hover:bg-wa-hover"
            data-testid="forward-modal-close-btn"
            disabled={sending}
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <MagnifyingGlass
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-wa-text-secondary"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats..."
              className="w-full pl-9 pr-3 py-2 border border-wa-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent"
              style={{ boxShadow: 'none' }}
              data-testid="forward-modal-search"
            />
          </div>
        </div>

        {/* Select All Toggle */}
        {filteredChats.length > 0 && (
          <div className="px-4 pb-2 flex items-center justify-between">
            <button
              onClick={toggleSelectAllFiltered}
              className="flex items-center gap-2 text-sm font-medium text-wa-text-primary hover:text-wa-text-primary"
              data-testid="forward-select-all-btn"
              disabled={sending}
            >
              {allFilteredSelected ? (
                <CheckSquare size={20} weight="fill" style={{ color: accentColor }} />
              ) : (
                <Square size={20} />
              )}
              <span>
                {allFilteredSelected ? 'Deselect all' : 'Select all'}
                {search ? ' (filtered)' : ''}
              </span>
            </button>
            {selected.size > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-wa-text-secondary hover:text-wa-text-primary underline"
                data-testid="forward-clear-selection-btn"
                disabled={sending}
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto px-4 pb-3">
          {filteredChats.length === 0 ? (
            <p className="text-wa-text-secondary text-center py-8 text-sm">
              {chats.length === 0 ? 'No other chats available' : 'No matches found'}
            </p>
          ) : (
            <div className="space-y-1.5">
              {filteredChats.map((chat) => {
                const isSelected = selected.has(chat.id);
                return (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => toggleOne(chat.id)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-left flex items-center gap-3 transition-colors ${
                      isSelected
                        ? 'border-transparent'
                        : 'border-wa-border hover:bg-wa-hover'
                    }`}
                    style={
                      isSelected
                        ? { backgroundColor: `${accentColor}15`, borderColor: accentColor }
                        : {}
                    }
                    data-testid={`forward-chat-row-${chat.id}`}
                    disabled={sending}
                  >
                    {/* Checkbox */}
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center"
                      style={
                        isSelected
                          ? { backgroundColor: accentColor, borderColor: accentColor }
                          : { borderColor: '#d1d5db' }
                      }
                    >
                      {isSelected && <Check size={14} weight="bold" color="#fff" />}
                    </div>

                    {/* Avatar */}
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#e5e7eb' }}
                    >
                      <UserCircle size={28} className="text-wa-text-secondary" />
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate text-sm">
                        {chat.customer_name || 'Unknown Customer'}
                      </div>
                      <div className="text-xs text-wa-text-secondary capitalize">
                        {chat.chat_type} chat
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t px-4 py-3 flex gap-2">
          <button
            onClick={handleSendToAll}
            disabled={chats.length === 0 || sending}
            className="flex-1 px-3 py-2.5 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ borderColor: accentColor, color: accentColor }}
            data-testid="forward-send-to-all-btn"
          >
            Send to All ({chats.length})
          </button>
          <button
            onClick={handleSendToSelected}
            disabled={selected.size === 0 || sending}
            className="flex-1 px-3 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: accentColor }}
            data-testid="forward-send-selected-btn"
          >
            {sending ? (
              'Sending...'
            ) : (
              <>
                <PaperPlaneRight size={16} weight="fill" />
                Send ({selected.size})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiSelectForwardModal;
